import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Settings, Fuel, Check, Calendar as CalendarIcon, ShieldCheck, ArrowRight, MessageCircle, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, PHP } from "@/lib/api";
import { DISCOUNT_TIERS, elNidoFeeFor } from "@/lib/policy";
import { waLink, waVehicleMessage } from "@/lib/contact";
import { Calendar } from "@/components/ui/calendar";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookedRanges, setBookedRanges] = useState([]);

  useEffect(() => {
    setLoading(true);
    api.get(`/vehicles/${id}`).then((r) => {
      setV(r.data);
      document.title = `${r.data.name} — Rent in Puerto Princesa, Palawan | PB-Lux`;
    }).catch(() => setV(null)).finally(() => setLoading(false));
    api.get(`/vehicles/${id}/booked-dates`).then((r) => setBookedRanges(r.data || [])).catch(() => {});
  }, [id]);

  const isBooked = (date) =>
    bookedRanges.some((r) => {
      const s = new Date(r.start); const e = new Date(r.end);
      return date >= new Date(s.getFullYear(), s.getMonth(), s.getDate()) && date < new Date(e.getFullYear(), e.getMonth(), e.getDate());
    });

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-32 max-w-5xl mx-auto px-6">
          <div className="aspect-[16/9] bg-ink-800 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (!v) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-40 max-w-3xl mx-auto px-6 text-center">
          <p className="font-display text-4xl">Vehicle not found.</p>
          <Link to="/fleet" className="btn-outline-gold mt-8 inline-flex">
            <ArrowLeft className="w-4 h-4" /> Back to Fleet
          </Link>
        </div>
      </div>
    );
  }

  const promo = Number(v.promo_rate) > 0 && Number(v.promo_rate) < Number(v.daily_rate);
  const displayRate = promo ? v.promo_rate : v.daily_rate;
  const elNidoFee = elNidoFeeFor(v);

  return (
    <div className="min-h-screen">
      <Navbar />

      <section className="hero-bg pt-28 sm:pt-32 pb-16 noise relative" data-testid="vehicle-detail">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/fleet" className="inline-flex items-center gap-2 text-sm text-gold-300 hover:text-gold-100 mb-8" data-testid="back-to-fleet">
            <ArrowLeft className="w-4 h-4" /> Back to Fleet
          </Link>

          <div className="grid lg:grid-cols-12 gap-10">
            {/* Image + specs + availability */}
            <div className="lg:col-span-7">
              <div className="relative rounded-3xl overflow-hidden border border-gold-500/15 bg-ink-800">
                <img src={v.image} alt={`${v.name} for rent in Puerto Princesa, Palawan`} className="w-full aspect-[4/3] object-cover" />
                <div className="absolute top-5 left-5 flex gap-2">
                  <span className="spec-pill capitalize">{v.category}</span>
                  {promo && <span className="text-[10px] uppercase tracking-widest bg-green-500/90 text-ink-900 px-2.5 py-1 rounded-full font-semibold">{v.promo_label || "Promo"}</span>}
                </div>
              </div>

              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {v.seats && (
                  <div className="glass-card rounded-xl p-4 text-center">
                    <Users className="w-5 h-5 text-gold-400 mx-auto" />
                    <p className="font-display text-2xl mt-2">{v.seats}</p>
                    <p className="text-[11px] uppercase tracking-widest text-foreground/50">Seats</p>
                  </div>
                )}
                <div className="glass-card rounded-xl p-4 text-center">
                  <Settings className="w-5 h-5 text-gold-400 mx-auto" />
                  <p className="font-display text-lg mt-2 capitalize">{v.transmission}</p>
                  <p className="text-[11px] uppercase tracking-widest text-foreground/50">Transmission</p>
                </div>
                {v.drivetrain && (
                  <div className="glass-card rounded-xl p-4 text-center">
                    <Fuel className="w-5 h-5 text-gold-400 mx-auto" />
                    <p className="font-display text-lg mt-2">{v.drivetrain}</p>
                    <p className="text-[11px] uppercase tracking-widest text-foreground/50">Drivetrain</p>
                  </div>
                )}
                {v.year && (
                  <div className="glass-card rounded-xl p-4 text-center">
                    <CalendarIcon className="w-5 h-5 text-gold-400 mx-auto" />
                    <p className="font-display text-lg mt-2">{v.year}</p>
                    <p className="text-[11px] uppercase tracking-widest text-foreground/50">Year</p>
                  </div>
                )}
              </div>

              {/* Availability calendar */}
              <div id="availability" className="mt-8 glass-card rounded-2xl p-6" data-testid="availability-calendar">
                <p className="kicker">Availability Calendar</p>
                <p className="text-xs text-foreground/55 mt-2">Greyed-out dates are already reserved. Pick your dates on the booking page — availability is re-checked before payment.</p>
                <div className="mt-4 flex justify-center">
                  <Calendar
                    mode="single"
                    disabled={(d) => d < new Date() || isBooked(d)}
                    className="rounded-xl border border-gold-500/15"
                  />
                </div>
              </div>
            </div>

            {/* Info & CTA */}
            <div className="lg:col-span-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold-500/80 font-sans-display">{v.brand}</p>
              <h1 className="font-display text-4xl sm:text-5xl mt-2">{v.model}</h1>
              <p className="text-foreground/50 mt-1">{v.name}</p>

              <div className="mt-7 glass-card rounded-2xl p-6">
                <div className="flex items-end gap-3">
                  <p className={`font-display text-5xl leading-none ${promo ? "text-green-300" : "text-gold-300"}`}>{PHP(displayRate)}</p>
                  <div className="pb-1">
                    <p className="text-foreground/50 text-sm">/ day</p>
                    {promo && <p className="text-xs text-foreground/40 line-through">{PHP(v.daily_rate)} regular</p>}
                  </div>
                </div>
                <p className="text-xs text-foreground/50 mt-2">
                  Reservation fee = one day's rental, fully deducted from your total
                </p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="discount-hint">
                  {DISCOUNT_TIERS.map((t) => (
                    <span key={t.range} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-500/15 border border-green-500/30 text-[10px] text-green-300">
                      {t.range}: {t.pct}% off
                    </span>
                  ))}
                </div>
                <button onClick={() => navigate(`/book/${v.id}`)} className="btn-gold w-full mt-6" data-testid="book-this-vehicle">
                  Reserve Now <ArrowRight className="w-4 h-4" />
                </button>
                <a href={waLink(waVehicleMessage(v))} target="_blank" rel="noreferrer"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-green-500/40 text-green-300 hover:bg-green-500/15 transition text-sm"
                  data-testid="vehicle-whatsapp">
                  <MessageCircle className="w-4 h-4" /> WhatsApp Us About This Unit
                </a>
              </div>

              <p className="mt-7 text-sm text-foreground/70 leading-relaxed">{v.description}</p>

              {/* Pickup / drop-off options */}
              <div className="mt-7 glass-card rounded-2xl p-6" data-testid="pickup-dropoff-options">
                <p className="kicker">Pickup & Drop-off Options</p>
                <ul className="mt-4 space-y-2 text-sm text-foreground/75">
                  <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-gold-400 flex-shrink-0" /> PB-Lux Shop, Puerto Princesa — Free</li>
                  <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-gold-400 flex-shrink-0" /> Puerto Princesa Airport / Hotel delivery — ₱250</li>
                  {v.type !== "motorbike" ? (
                    <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-gold-400 flex-shrink-0" /> El Nido pickup or drop-off — {PHP(elNidoFee)} per leg (daily rate unchanged)</li>
                  ) : (
                    <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-gold-400 flex-shrink-0" /> El Nido pickup/delivery (incl. El Nido Airport) — {PHP(elNidoFee)} per leg (daily rate unchanged)</li>
                  )}
                </ul>
              </div>

              {v.features?.length > 0 && (
                <div className="mt-7">
                  <p className="kicker">Features</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {v.features.map((f) => <span key={f} className="spec-pill">{f}</span>)}
                  </div>
                </div>
              )}

              {v.inclusions?.length > 0 && (
                <div className="mt-7">
                  <p className="kicker">Included</p>
                  <ul className="mt-4 space-y-2">
                    {v.inclusions.map((inc) => (
                      <li key={inc} className="flex items-center gap-3 text-sm text-foreground/75">
                        <Check className="w-4 h-4 text-gold-400" /> {inc}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-7 glass-card rounded-xl p-5 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-gold-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-foreground/65 leading-relaxed">
                  <span className="text-gold-200 font-medium">Requirements: </span>
                  Driver's License + 1 valid government ID. Security your choice: valid passport (subject to verification & approval) OR refundable deposit of <span className="text-gold-200">{v.type === "motorbike" ? "₱3,000" : "₱5,000"}</span>.
                  Minimum 2 days for outside-city drives. <Link to="/insurance-policy" className="text-gold-300 hover:text-gold-100 underline underline-offset-2" data-testid="view-insurance-policy">View Insurance Policy</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
